/**
 * Shared chart-label formatting for the report graphs.
 *
 * The backend emits series labels in lower case and without units (e.g.
 * "fuel consumed", "engine hours", "energy"). For the on-screen legend and the
 * hover tooltip we want them capitalised and suffixed with the graph's axis
 * unit so a legend/tooltip entry reads e.g. "Fuel consumed (L)" rather than a
 * bare lower-case token. This is presentation only — the underlying series key
 * and values are untouched.
 */

/** Capitalise the first character of a label, leaving the rest as authored. */
function capitaliseFirst(label: string): string {
  if (!label) return label
  return label.charAt(0).toUpperCase() + label.slice(1)
}

/**
 * Format a series label for display: capitalise the first letter and append the
 * axis unit in parentheses when a unit is present and not already in the label.
 *
 * @param rawLabel The series label from the backend (may be lower case), or its
 *   key as a fallback.
 * @param unit The graph's axis unit (e.g. "L", "kWh", "%"), or empty/undefined.
 */
export function formatSeriesLabel(rawLabel: string, unit?: string | null): string {
  const base = capitaliseFirst(rawLabel.trim())
  const u = (unit ?? "").trim()
  if (!u) return base
  // Avoid doubling up only when the label already carries the unit in its
  // parenthesised form (e.g. "State of charge (%)"). Matching a bare trailing
  // substring instead would suppress a legitimate suffix whenever a label just
  // happens to end with the unit's letters (unit "h" on "…kWh", unit "L" on a
  // label ending "…L"), so the guard is limited to the "(unit)" form.
  if (base.includes(`(${u})`)) return base
  return `${base} (${u})`
}
